import { ChangeDetectionStrategy, Component, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { IdentityService } from 'src/app/auth/identity.service';
import { AnagraficheService } from 'src/app/core/anagrafiche.service';
import { autocompleteFilter } from 'src/app/shared/helpers/autocompleteFilter.helper';
import { Documento } from 'src/app/shared/models/documento.model';
import { DocumentoRiproduzione } from 'src/app/shared/models/documentoRiproduzione.model';
import { Indirizzo } from 'src/app/shared/models/indirizzo.model';
import { Posizione } from 'src/app/shared/models/posizione.model';
import { Utente } from 'src/app/shared/models/utente.model';

@Component({
  selector: 'app-riproduzioni-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <p-dialog header="Riproduzioni" [(visible)]="visible" [modal]="true" (onHide)="hideDialog()" [style]="{'width':'80%'}" >
    <app-position-select [(ngModel)]="posizione" />
    <hr>
    <form [formGroup]="riproduzioniForm">
      <div class="grid" >
        <div class="col-4">
          <label for="numeroRichiesta" class="small-label">N° richiesta</label>
          <input id="numeroRichiesta" class="width-fill-available" pInputText type="text" formControlName="numeroRichiesta">
        </div>
        <div class="col-4">
          <label for="utente" class="small-label">Utente</label>
          <p-autoComplete inputId="utente" formControlName="richiedente" [suggestions]="utentiFiltrati()" [style]="{'width':'100%'}" (completeMethod)="filterUtenti($event)" [dropdown]="true" [forceSelection]="false" [autoHighlight]="true" appendTo="body">
          </p-autoComplete>
        </div>
        <div class="col-4">
          <label for="dataRichiesta" class="small-label">Data richiesta</label>
          <p-calendar id="dataRichiesta" [showIcon]="true" [style]="{'width':'100%'}" formControlName="dataRichiesta" dateFormat="dd/mm/yy" [selectOtherMonths]="true" appendTo="body"></p-calendar>
        </div>
      </div>
      <hr>

      <div class="grid">
        <div class="col-4">
          <label for="enteRichiedente" class="small-label">Ente Richiedente</label>
          <app-dati-documento-destinatari  id="enteRichiedente" formControlName="destinatariArray" (addIndirizzo)="addIndirizzo($event)" tooltipPosition="left" />
        </div>
        <div class="col-4">
          <label for="originatore" class="small-label">Originatore</label>
          <input id="originatore" class="width-fill-available" pInputText type="text" formControlName="originatore">
        </div>
        <div class="col-2">
          <label for="numeroPagine" class="small-label">Numero Pagine</label>
          <input id="numeroPagine" class="width-fill-available" pInputText type="text" formControlName="numeroPagine">
        </div>
        <div class="col-2">
          <label class="small-label">Contenuto</label>
          <div class="content-choice">
            <p-checkbox inputId="includeDocumentoPrimario" [binary]="true" formControlName="includeDocumentoPrimario"></p-checkbox>
            <label for="includeDocumentoPrimario">Documento primario</label>
          </div>
          <div class="content-choice">
            <p-checkbox inputId="includeAllegati" [binary]="true" formControlName="includeAllegati"></p-checkbox>
            <label for="includeAllegati">Allegati</label>
          </div>
        </div>
      </div>
    </form>
    <p-footer>
      <button pButton type="button" icon="pi pi-times" label="Annulla" (click)="hideDialog()" class="p-button-secondary"></button>
      <button pButton [disabled]="isSaveDisabled()" type="button" icon="pi pi-check" label="Salva" (click)="saveClick()"></button>
    </p-footer>
  </p-dialog>
  `,
  styles: [`
    label { margin-bottom: 4px; margin-top: 20px; color: inherit; }
    label:first-of-type { margin-top: 0; }
    .width-fill-available{ width: 100%; }
    .content-choice { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
    .content-choice label { margin: 0; }
  `]
})
 
export class RiproduzioniDialogComponent {
  positionRiproduzione = input<Posizione[] | null>(null);
  visible = model.required<boolean>();
  documento = input.required<Documento>();
  hide = output();
  save = output<RiproduzioneEvent>();

  public riproduzione: DocumentoRiproduzione = { dataRichiesta: new Date() } as DocumentoRiproduzione;
  public utenti: Utente[] = [];
  public utentiFiltrati = signal<Utente[]>([]);

  public autocompleteFilter = autocompleteFilter;
  public destinatariArray: Indirizzo[] = [];
  public posizione: Posizione[] | null = null;

  public riproduzioniForm = new FormGroup({
    numeroRichiesta: new FormControl('', [Validators.required]),
    richiedente: new FormControl('', [Validators.required]),
    dataRichiesta: new FormControl(new Date(), [Validators.required]),
    destinatariArray: new FormControl([]),
    originatore: new FormControl('', [Validators.required]),
    numeroPagine: new FormControl('', [Validators.required]),
    includeDocumentoPrimario: new FormControl(true),
    includeAllegati: new FormControl(true),
  });

  private readonly identityService = inject(IdentityService);
  private readonly anagraficheService = inject(AnagraficheService);

  constructor() {
    effect(() => {
      if (!this.visible() || !this.documento()) return;

      this.caricaUtenti();
      this.posizione = this.positionRiproduzione();
      untracked(() => {
        this.configuraForm();
      });
    });
  }

  private caricaUtenti(): void {
    this.anagraficheService.utenti.getAllArray(u => this.utenti = u);
  }

  private configuraForm(): void {
    this.riproduzioniForm.patchValue({
      dataRichiesta: new Date(),
      richiedente: this.identityService.userName,
      includeDocumentoPrimario: true,
      includeAllegati: true,
    });

    this.anagraficheService.indirizzi.getAllArray(i => {
      const mittente = i.find(ind => ind.indirizzoId === this.documento().mittenteId);
      if (mittente) {
        this.riproduzioniForm.patchValue({ originatore: mittente.nomeIndirizzo });
      }
    });
  }

  public hideDialog(): void {
    this.riproduzioniForm.reset();
    this.hide.emit();
  }

  public saveClick(): void {
    if (!this.posizione) return;

    this.fillOutput();

    this.save.emit({ dettagli: this.riproduzione, destinatari: this.destinatariArray, posizione: this.posizione });

    this.hideDialog();
  }

  private fillOutput(): void {
    this.riproduzione = {
      richiedente: this.riproduzioniForm.get('richiedente')?.value ?? '',
      numeroRichiesta: this.riproduzioniForm.get('numeroRichiesta')?.value ?? '',
      dataRichiesta: this.riproduzioniForm.get('dataRichiesta')?.value ?? new Date(),
      originatore: this.riproduzioniForm.get('originatore')?.value ?? '',
      numeroPagine: this.riproduzioniForm.get('numeroPagine')?.value ?? '',
      soloAllegati: !this.includeDocumentoPrimario && this.includeAllegati,
      includeDocumentoPrimario: this.includeDocumentoPrimario,
      includeAllegati: this.includeAllegati,
    } as DocumentoRiproduzione;

    this.destinatariArray = this.riproduzioniForm.get('destinatariArray')?.value ?? [];
  }

  public addIndirizzo(name: string | null): void {
    if (name) {
      const ind = {
        isConfirmed: false,
        nomeIndirizzo: name
      } as Indirizzo;

      this.anagraficheService.indirizzi.create(ind).subscribe();
    }
  }

  public isSaveDisabled(): boolean {
    return (!this.riproduzioniForm.valid || !this.posizione || (!this.includeDocumentoPrimario && !this.includeAllegati));
  }

  public filterUtenti(event: AutoCompleteCompleteEvent) {
    this.utentiFiltrati.set(this.autocompleteFilter(event, this.utenti, 'nomeUtente', true))
  }

  private get includeDocumentoPrimario(): boolean {
    return this.riproduzioniForm.get('includeDocumentoPrimario')?.value ?? false;
  }

  private get includeAllegati(): boolean {
    return this.riproduzioniForm.get('includeAllegati')?.value ?? false;
  }
}

export interface RiproduzioneEvent {
  dettagli: DocumentoRiproduzione;
  destinatari: Indirizzo[];
  posizione: Posizione[];
}
